import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getErrorMessage } from '@/utils/errorHandler';
import { FullScreenSignupLayout } from '@/components/ui/full-screen-signup';
import { apiService } from '@/services/api';
import { FormInput, FormLabel } from '@/components/ui/form-primitives';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/useToast';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!token) {
      setTokenError('Missing reset token.');
      setValidating(false);
      return;
    }

    let cancelled = false;

    const validate = async () => {
      try {
        const response = await apiService.client.get(`/api/auth/password-reset/validate/${token}`);
        if (cancelled) return;
        const body = response.data?.data;
        if (body?.isValid) {
          setTokenValid(true);
        } else {
          setTokenError(body?.message || 'Invalid or expired reset link.');
        }
      } catch {
        if (!cancelled) {
          setTokenError('Unable to validate reset link. Please request a new one.');
        }
      } finally {
        if (!cancelled) setValidating(false);
      }
    };

    void validate();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);

    try {
      await apiService.client.post('/api/auth/password-reset/confirm', {
        token,
        newPassword: password,
        confirmPassword,
      });
      toast.success('Password reset successfully. You can now log in.');
      navigate('/login', { replace: true });
    } catch (error: unknown) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <FullScreenSignupLayout
        leftHeadline=""
        rightTitle="Reset password"
        rightSubtitle="Verifying your reset link…"
      >
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary/40 border-t-primary" />
        </div>
      </FullScreenSignupLayout>
    );
  }

  if (!tokenValid) {
    return (
      <FullScreenSignupLayout
        leftHeadline=""
        rightTitle="Invalid link"
        rightSubtitle="This reset link is invalid or has expired"
      >
        <div className="flex flex-col gap-5">
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 flex gap-3 text-sm">
            <span className="material-symbols-outlined text-lg shrink-0 text-amber-600">link_off</span>
            <p className="font-medium pt-0.5 text-amber-900">{tokenError || 'Invalid or expired reset link.'}</p>
          </div>
          <Link
            to="/forgot-password"
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-primary hover:bg-[#2d3166] text-white rounded-lg font-medium shadow-md shadow-[#393e7f]/20 transition-colors"
          >
            Request a new link
          </Link>
        </div>
      </FullScreenSignupLayout>
    );
  }

  return (
    <FullScreenSignupLayout
      leftHeadline=""
      rightTitle="Set new password"
      rightSubtitle="Choose a strong password for your account"
    >
      <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
        <div className="space-y-2">
          <FormLabel htmlFor="reset-password">
            New password
          </FormLabel>
          <div className="relative">
            <FormInput
              id="reset-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pr-11"
              placeholder="••••••••"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-[#393e7f]"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <span className="material-symbols-outlined text-xl">
                {showPassword ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>
          <p className="text-xs text-slate-500">At least 8 characters, with upper, lower, number, and special character.</p>
        </div>

        <div className="space-y-2">
          <FormLabel htmlFor="reset-confirm">
            Confirm password
          </FormLabel>
          <FormInput
            id="reset-confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
          />
        </div>

        <Button
          type="submit"
          loading={loading}
          disabled={!password || !confirmPassword}
          className="w-full min-h-12"
          size="xl"
        >
          Reset password
        </Button>
      </form>

      <div className="mt-4 text-center text-sm text-slate-600">
        <Link to="/login" className="font-semibold text-[#393e7f] hover:text-[#2d3166] underline underline-offset-2">
          Back to login
        </Link>
      </div>
    </FullScreenSignupLayout>
  );
};

export default ResetPasswordPage;
